import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { plans as plansApi } from '@/api/plans';
import type { PlanRow, CreatePlanInput } from '@/api/plans';
import { billing } from '@/api/billing';
import type { StripePriceRow } from '@/api/billing';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface FormValues {
  name: string;
  monthly_price_dollars: string;
  included_channels: string;
  included_minutes: string;
  included_storage_gb: string;
  included_active_dids: string;
  stripe_price_id: string;
  extra_channel_cents: string;
  extra_minute_cents: string;
  extra_storage_gb_cents: string;
  extra_did_cents: string;
}

function dollarsToCents(s: string): number {
  return Math.round(parseFloat(s || '0') * 100);
}

function centsToDisplay(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** "Pro Plan — $49.00/month" (or "…one-time" / "variable"). */
function priceLabel(p: StripePriceRow): string {
  const name = p.product_name ?? p.nickname ?? p.id;
  let amount: string;
  if (p.unit_amount == null) {
    amount = 'variable';
  } else {
    try {
      amount = (p.unit_amount / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: p.currency.toUpperCase(),
      });
    } catch {
      // Unknown ISO code — Intl throws RangeError. Fall back to plain.
      amount = `${(p.unit_amount / 100).toFixed(2)} ${p.currency.toUpperCase()}`;
    }
  }
  const interval = p.recurring_interval ? `/${p.recurring_interval}` : ' one-time';
  return `${name} — ${amount}${interval}`;
}

const NO_PRICE = '__none__';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: PlanRow | null;
}

function FieldRow({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] items-center gap-3">
      <Label htmlFor={id} className="text-right text-sm">{label}</Label>
      {children}
    </div>
  );
}

export function PlanDialog({ open, onOpenChange, existing }: Props) {
  const qc = useQueryClient();
  const isEdit = !!existing;

  const { register, handleSubmit, reset, watch, setValue, formState: { isSubmitting } } = useForm<FormValues>({
    defaultValues: {
      name: '',
      monthly_price_dollars: '0',
      included_channels: '5',
      included_minutes: '1000',
      included_storage_gb: '10',
      included_active_dids: '2',
      stripe_price_id: '',
      extra_channel_cents: '1000',
      extra_minute_cents: '2',
      extra_storage_gb_cents: '50',
      extra_did_cents: '200',
    },
  });

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        monthly_price_dollars: centsToDisplay(existing.monthly_price_cents),
        included_channels: String(existing.included_channels),
        included_minutes: String(existing.included_minutes),
        included_storage_gb: String(existing.included_storage_gb),
        included_active_dids: String(existing.included_active_dids),
        stripe_price_id: existing.stripe_price_id ?? '',
        extra_channel_cents: String(existing.overage_rates.extra_channel_cents),
        extra_minute_cents: String(existing.overage_rates.extra_minute_cents),
        extra_storage_gb_cents: String(existing.overage_rates.extra_storage_gb_cents),
        extra_did_cents: String(existing.overage_rates.extra_did_cents),
      });
    } else {
      reset({
        name: '',
        monthly_price_dollars: '0',
        included_channels: '5',
        included_minutes: '1000',
        included_storage_gb: '10',
        included_active_dids: '2',
        stripe_price_id: '',
        extra_channel_cents: '1000',
        extra_minute_cents: '2',
        extra_storage_gb_cents: '50',
        extra_did_cents: '200',
      });
    }
  }, [existing, reset, open]);

  // Active Stripe prices for the picker (rule 13 — no raw ID text
  // inputs). When Stripe isn't configured the endpoint answers 200 with
  // stripe_configured=false and we fall back to the manual input below.
  const { data: priceData, isLoading: pricesLoading, isError: pricesError } = useQuery({
    queryKey: ['stripe-prices'],
    queryFn: () => billing.listStripePrices(),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const priceRows = priceData?.rows ?? [];
  const usePricePicker = !!priceData && priceData.stripe_configured && priceRows.length > 0;
  const stripePriceId = watch('stripe_price_id');
  // Plan already points at a price that's no longer in the active list
  // (archived / other mode). Keep it selectable so opening the dialog
  // doesn't silently blank the field.
  const currentPriceMissing = !!stripePriceId && !priceRows.some((p) => p.id === stripePriceId);

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const input: CreatePlanInput = {
        name: data.name,
        monthly_price_cents: dollarsToCents(data.monthly_price_dollars),
        included_channels: parseInt(data.included_channels, 10),
        included_minutes: parseInt(data.included_minutes, 10),
        included_storage_gb: parseInt(data.included_storage_gb, 10),
        included_active_dids: parseInt(data.included_active_dids, 10),
        stripe_price_id: data.stripe_price_id || null,
        overage_rates: {
          extra_channel_cents: parseInt(data.extra_channel_cents, 10),
          extra_minute_cents: parseInt(data.extra_minute_cents, 10),
          extra_storage_gb_cents: parseInt(data.extra_storage_gb_cents, 10),
          extra_did_cents: parseInt(data.extra_did_cents, 10),
        },
        enabled_modules: [],
      };
      if (isEdit && existing) {
        return plansApi.update(existing.id, input);
      }
      return plansApi.create(input);
    },
    onSuccess: () => {
      toast({ title: isEdit ? 'Plan updated.' : 'Plan created.' });
      void qc.invalidateQueries({ queryKey: ['plans'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast({
        title: 'Failed to save plan',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit plan' : 'New plan'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4 py-2">
          <FieldRow label="Name" id="name">
            <Input id="name" placeholder="e.g. Pro" {...register('name', { required: true })} />
          </FieldRow>
          <FieldRow label="Monthly price ($)" id="price">
            <Input id="price" type="number" step="0.01" min="0" {...register('monthly_price_dollars')} />
          </FieldRow>

          <div className="my-3 border-t border-border/50 pt-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 pl-[172px]">Included limits</p>
          </div>

          <FieldRow label="Channels" id="channels">
            <Input id="channels" type="number" min="0" {...register('included_channels')} />
          </FieldRow>
          <FieldRow label="Minutes" id="minutes">
            <Input id="minutes" type="number" min="0" {...register('included_minutes')} />
          </FieldRow>
          <FieldRow label="Storage (GB)" id="storage">
            <Input id="storage" type="number" min="0" {...register('included_storage_gb')} />
          </FieldRow>
          <FieldRow label="DIDs" id="dids">
            <Input id="dids" type="number" min="0" {...register('included_active_dids')} />
          </FieldRow>

          <div className="my-3 border-t border-border/50 pt-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 pl-[172px]">Overage rates (cents)</p>
          </div>

          <FieldRow label="Per extra channel" id="oc">
            <Input id="oc" type="number" min="0" {...register('extra_channel_cents')} />
          </FieldRow>
          <FieldRow label="Per extra minute" id="om">
            <Input id="om" type="number" min="0" {...register('extra_minute_cents')} />
          </FieldRow>
          <FieldRow label="Per extra GB" id="os">
            <Input id="os" type="number" min="0" {...register('extra_storage_gb_cents')} />
          </FieldRow>
          <FieldRow label="Per extra DID" id="od">
            <Input id="od" type="number" min="0" {...register('extra_did_cents')} />
          </FieldRow>

          <div className="my-3 border-t border-border/50 pt-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 pl-[172px]">Stripe</p>
          </div>

          <FieldRow label="Stripe Price" id="spi">
            {usePricePicker ? (
              <div className="space-y-1">
                <Select
                  value={stripePriceId || NO_PRICE}
                  onValueChange={(v) =>
                    setValue('stripe_price_id', v === NO_PRICE ? '' : v, { shouldDirty: true })
                  }
                >
                  <SelectTrigger id="spi" className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PRICE} className="text-sm">
                      <span className="italic text-muted-foreground">No Stripe price — manual billing</span>
                    </SelectItem>
                    {currentPriceMissing && (
                      <SelectItem value={stripePriceId} className="text-sm">
                        <span className="flex flex-col items-start">
                          <span className="font-mono text-xs">{stripePriceId}</span>
                          <span className="text-[10px] text-amber-600">current value — not in Stripe&rsquo;s active list</span>
                        </span>
                      </SelectItem>
                    )}
                    {priceRows.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-sm">
                        <span className="flex flex-col items-start">
                          <span>{priceLabel(p)}</span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {p.id}{p.nickname && p.product_name ? ` · ${p.nickname}` : ''}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {stripePriceId && (
                  <p className="font-mono text-[10px] text-muted-foreground">{stripePriceId}</p>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <Input
                  id="spi"
                  placeholder={pricesLoading ? 'Checking Stripe…' : 'price_…'}
                  className="font-mono text-sm"
                  disabled={pricesLoading}
                  {...register('stripe_price_id')}
                />
                {!pricesLoading && (
                  <p className="text-[11px] text-muted-foreground">
                    {pricesError
                      ? 'Couldn’t load prices from Stripe — paste the price ID manually.'
                      : priceData && !priceData.stripe_configured
                        ? 'Stripe isn’t configured yet (Settings → Stripe billing), so the price list can’t be loaded. Paste the price ID manually.'
                        : 'No active prices found in your Stripe account — create one in Stripe, or paste an ID manually.'}
                  </p>
                )}
              </div>
            )}
          </FieldRow>

          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button type="submit" size="sm" disabled={mutation.isPending || isSubmitting}>
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create plan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
